import Papa from 'papaparse';
import { parseWfDate } from './date';

// Columns accepted from the uploaded CSV — mirrors the Deal shape (types/index.ts),
// excluding fields that are always derived rather than user-provided (age, market_area).
export const UNTRACKED_CSV_COLUMNS = [
  'id', 'account', 'sc', 'status', 'status_eq', 'region', 'sub_region',
  'close_q', 'offering', 'requestName', 'requestID', 'products', 'entry',
  'completion', 'sales_region', 'adjusted_commit', 'gnarr', 'opp_gnarr',
  'created_q', 'created_fy', 'raw_sub',
] as const;

const REQUIRED_COLUMNS = ['id', 'requestID'] as const;

export interface ParsedCsvRow {
  raw: Record<string, string>;
  index: number;
  issues: string[];
}

export interface ParsedCsvResult {
  rows: ParsedCsvRow[];
  columns: string[];
  fileErrors: string[];
}

function validateRow(raw: Record<string, string>): string[] {
  const issues: string[] = [];
  for (const col of REQUIRED_COLUMNS) {
    if (!raw[col]?.trim()) issues.push(`missing ${col}`);
  }
  // entry/completion must be dd/MM/yy — this is what daysBetween()/age computation expects
  // uniformly for every deal, synced or uploaded.
  if (raw.entry?.trim() && !parseWfDate(raw.entry)) issues.push('entry is not dd/MM/yy');
  if (raw.completion?.trim() && !parseWfDate(raw.completion)) issues.push('completion is not dd/MM/yy');
  return issues;
}

export function parseUntrackedCsv(file: File): Promise<ParsedCsvResult> {
  return new Promise(resolve => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim(),
      complete: result => {
        const columns = result.meta.fields ?? [];
        const rows: ParsedCsvRow[] = result.data.map((raw, index) => ({
          raw,
          index,
          issues: validateRow(raw),
        }));
        const fileErrors = result.errors.map(e => `Row ${e.row ?? '?'}: ${e.message}`);
        resolve({ rows, columns, fileErrors });
      },
      error: err => {
        resolve({ rows: [], columns: [], fileErrors: [err.message] });
      },
    });
  });
}
